import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, Building2, Plus, RefreshCw, Search, Telescope, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

type ManufacturerRow = {
  id: number
  name: string
  canonical_name: string
  is_own: boolean
  country: string | null
  alias_count: number
  researched: boolean
  last_researched_at: string | null
}

type Filters = {
  search: string | null
  owned: string | null
  researched: string | null
  sort_by: string
  sort_order: string
}

type PageProps = {
  manufacturers: ManufacturerRow[]
  pagination: { total_count: number; page: number; per_page: number }
  filters: Filters
  stats: { total: number; own: number; researched: number }
}

const DESCRIPTION =
  "Registry manufaktur — perusahaan sendiri dan kompetitor — diturunkan dari universe NIQ, dinormalisasi dan dideduplikasi ke satu nama kanonik."

export default function Index({ manufacturers, pagination, filters, stats }: PageProps) {
  const [search, setSearch] = React.useState(filters.search ?? "")
  const [selected, setSelected] = React.useState<number[]>([])

  // Drop selections that are no longer on the page (e.g. after a reload/delete).
  React.useEffect(() => {
    setSelected((prev) => prev.filter((id) => manufacturers.some((m) => m.id === id)))
  }, [manufacturers])

  const applyFilters = (next: Partial<Filters> & { page?: string }) => {
    const merged = {
      search,
      owned: filters.owned ?? "",
      researched: filters.researched ?? "",
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      ...next,
    }
    const query: Record<string, string> = {}
    Object.entries(merged).forEach(([k, v]) => {
      if (v != null && v !== "") query[k] = String(v)
    })
    router.get("/admin/manufacturers", query, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search })
  }

  const toggleSort = (column: string) => {
    const sort_order =
      filters.sort_by === column && filters.sort_order === "asc" ? "desc" : "asc"
    applyFilters({ sort_by: column, sort_order })
  }

  const seedManufacturers = () => {
    if (
      !confirm(
        "Sinkronkan daftar manufaktur dari NIQ? Manufaktur baru akan ditambahkan; yang sudah ada (termasuk hasil kurasi manual) tetap dipertahankan.",
      )
    )
      return
    router.post("/admin/manufacturers/seed", {}, { preserveScroll: true })
  }

  const allOnPageSelected =
    manufacturers.length > 0 && selected.length === manufacturers.length
  const toggleSelectAll = () =>
    setSelected(allOnPageSelected ? [] : manufacturers.map((m) => m.id))
  const toggleSelect = (id: number) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const deleteSelected = () => {
    if (selected.length === 0) return
    if (!confirm(`Hapus ${selected.length} manufaktur terpilih? Tindakan ini tidak dapat dibatalkan.`))
      return
    router.delete("/admin/manufacturers/bulk_destroy", {
      data: { ids: selected },
      preserveScroll: true,
    })
  }

  const totalPages = Math.max(1, Math.ceil(pagination.total_count / pagination.per_page))

  const sortIcon = (column: string) => {
    if (filters.sort_by !== column) return null
    return filters.sort_order === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  return (
    <>
      <Head title="Manufaktur">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Manufaktur" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Intelligence</span>
          <span>/</span>
          <span className="text-ink-body font-medium">Manufaktur</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Manufaktur</h1>
              <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={seedManufacturers}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sinkronkan dari NIQ
              </Button>
              <Button asChild>
                <Link href="/admin/manufacturers/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Manufaktur baru
                </Link>
              </Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>{stats.total} manufaktur</span>
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" /> {stats.own} perusahaan sendiri
            </span>
            <span className="inline-flex items-center gap-1">
              <Telescope className="h-3.5 w-3.5" /> {stats.researched} sudah diriset
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[200px]">
            <label htmlFor="search">Cari</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nama, nama kanonik, atau negara"
                className="pl-9"
              />
            </div>
          </form>
          <div className="min-w-[150px]">
            <label htmlFor="owned">Kepemilikan</label>
            <Select
              id="owned"
              className="mt-1"
              value={filters.owned ?? ""}
              onChange={(e) => applyFilters({ owned: e.target.value })}
            >
              <option value="">Semua</option>
              <option value="true">Perusahaan sendiri</option>
              <option value="false">Kompetitor</option>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label htmlFor="researched">Riset</label>
            <Select
              id="researched"
              className="mt-1"
              value={filters.researched ?? ""}
              onChange={(e) => applyFilters({ researched: e.target.value })}
            >
              <option value="">Semua</option>
              <option value="true">Sudah diriset</option>
              <option value="false">Belum diriset</option>
            </Select>
          </div>
        </div>

        {/* Bulk-delete bar */}
        {selected.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-hairline bg-surface px-4 py-3">
            <span className="text-sm text-ink-body">{selected.length} terpilih</span>
            <Button variant="danger" size="sm" onClick={deleteSelected}>
              <Trash2 className="mr-2 h-4 w-4" />
              Hapus terpilih ({selected.length})
            </Button>
          </div>
        )}

        {/* List */}
        {manufacturers.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              Belum ada manufaktur. Klik <strong>Sinkronkan dari NIQ</strong> untuk menurunkan daftar
              dari universe NIQ, atau <strong>Manufaktur baru</strong> untuk menambah manual.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <Checkbox
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        aria-label="Pilih semua"
                      />
                    </th>
                    <th className="cursor-pointer px-4 py-3 font-medium" onClick={() => toggleSort("name")}>
                      Nama{sortIcon("name")}
                    </th>
                    <th className="px-4 py-3 font-medium">Nama kanonik</th>
                    <th
                      className="cursor-pointer px-4 py-3 font-medium"
                      onClick={() => toggleSort("country")}
                    >
                      Negara{sortIcon("country")}
                    </th>
                    <th className="px-4 py-3 font-medium">Penanda</th>
                  </tr>
                </thead>
                <tbody>
                  {manufacturers.map((m) => (
                    <tr key={m.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selected.includes(m.id)}
                          onChange={() => toggleSelect(m.id)}
                          aria-label={`Pilih ${m.name}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Link href={`/admin/manufacturers/${m.id}`} className="font-medium">
                          {m.name}
                        </Link>
                        {m.alias_count > 0 && (
                          <div className="text-xs text-ink-muted">+{m.alias_count} alias</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-muted">{m.canonical_name}</td>
                      <td className="px-4 py-3 text-ink-body">{m.country ?? "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {m.is_own && (
                            <Badge tone="accent">
                              <Building2 className="mr-1 h-3 w-3" />
                              Sendiri
                            </Badge>
                          )}
                          {m.researched && (
                            <Badge tone="signal">
                              <Telescope className="mr-1 h-3 w-3" />
                              Riset
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {manufacturers.map((m) => (
                <div key={m.id} className="rounded-lg border border-hairline bg-surface p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selected.includes(m.id)}
                      onChange={() => toggleSelect(m.id)}
                      aria-label={`Pilih ${m.name}`}
                      className="mt-1"
                    />
                    <Link href={`/admin/manufacturers/${m.id}`} className="min-w-0 flex-1">
                      <div className="font-medium text-ink-body">{m.name}</div>
                      <div className="text-xs text-ink-muted">{m.canonical_name}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.is_own && <Badge tone="accent">Sendiri</Badge>}
                        {m.researched && <Badge tone="signal">Riset</Badge>}
                        {m.country && <Badge tone="neutral">{m.country}</Badge>}
                        {m.alias_count > 0 && <Badge tone="muted">+{m.alias_count} alias</Badge>}
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
            <span>
              Halaman {pagination.page} dari {totalPages} ({pagination.total_count} manufaktur)
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => applyFilters({ page: String(pagination.page - 1) })}
              >
                Sebelumnya
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => applyFilters({ page: String(pagination.page + 1) })}
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </AppShell>
    </>
  )
}
